<script setup lang="ts">
const props = withDefaults(
    defineProps<{
        title: string
        icon: string
        data: number[]
        unit?: string
        color?: string
        type?: 'trend' | 'bar'
        fill?: boolean
        precision?: number
    }>(),
    {
        unit: '',
        color: '#5470C6',
        type: 'trend',
        fill: false,
        precision: 0,
    },
)

const theme = useTheme()

const current = computed(() => (props.data.length ? props.data[0] : null))
const min = computed(() => (props.data.length ? Math.min(...props.data) : null))
const max = computed(() => (props.data.length ? Math.max(...props.data) : null))

const fmt = (n: number | null) => (n === null ? '—' : n.toFixed(props.precision))

const gradient = computed(() =>
    props.fill ? [`${props.color}59`, `${props.color}08`] : [props.color],
)
</script>

<template>
    <v-card class="metric-card" rounded="xl" elevation="0">
        <div class="metric-head">
            <v-icon :icon="icon" size="16" :color="color" />
            <span class="metric-title">{{ title }}</span>
        </div>

        <div class="metric-value">
            {{ fmt(current) }}<span class="metric-unit">{{ unit }}</span>
        </div>

        <div class="metric-spark">
            <v-sparkline
                v-if="data.length"
                :model-value="data"
                :type="type"
                :color="color"
                :fill="fill"
                :gradient="gradient"
                gradient-direction="top"
                :line-width="type === 'bar' ? 8 : 2.5"
                :smooth="type === 'trend'"
                :padding="type === 'bar' ? 1 : 4"
                auto-draw
                height="38"
            />
        </div>

        <div class="metric-foot">
            <span>min {{ fmt(min) }}{{ unit }}</span>
            <span>max {{ fmt(max) }}{{ unit }}</span>
        </div>
    </v-card>
</template>

<style scoped>
.metric-card {
    height: 100%;
    padding: 12px 14px 10px;
    background: v-bind('theme.global.name.value === "dark" ? "rgba(30, 38, 56, 0.55)" : "rgba(255, 255, 255, 0.8)"') !important;
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border: 1px solid v-bind('theme.global.name.value === "dark" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.5)"');
}

.metric-head {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
}

.metric-title {
    font-size: 0.78rem;
    font-weight: 600;
    opacity: 0.75;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.metric-value {
    font-size: 1.6rem;
    font-weight: 700;
    line-height: 1.2;
    margin-top: 2px;
}

.metric-unit {
    font-size: 0.95rem;
    font-weight: 500;
    opacity: 0.7;
}

.metric-spark {
    margin: 2px 0 4px;
}

.metric-foot {
    display: flex;
    justify-content: space-between;
    font-size: 0.68rem;
    opacity: 0.55;
}
</style>
